import React, { useMemo, useRef, useCallback } from 'react';
import '../styles/AllCompsMap.css';
import { GoogleMap } from '@react-google-maps/api';
import { MapModeToggle, Marker, NeighbourhoodGradeMap } from './';
import { House, MapBubble } from '../assets/icons';
import { colour } from '../styles/GlobalStyles';

function AllCompsMap(props) {
    const subjectProperty = props.subjectProperty;
    const isMapLoaded = props.isMapLoaded;
    const comps = props.comps;
    const clickedZPID = props.clickedZPID;
    const viewComp = props.viewComp;
    // const bestComps = props.bestComps;
    const hoverZPID = props.hoverZPID;
    const neighbourhoodData = props.neighbourhoodData;
    const tracts = props.tracts;
    const neighbourhoodBounds = props.neighbourhoodBounds;
    const hoverMapLegend = props.hoverMapLegend;
    const setHoverMapLegend = props.setHoverMapLegend;
    const showHeatmap = props.showHeatmap;
    const readOnly = props.readOnly;
    const compTypeLegend = props.compTypeLegend;
    const innerWidth = window.innerWidth;
    const mapType = props.mapType;
    const setMapType = props.setMapType;
    const rawMap = props.rawMap;
    const setRawMap = props.setRawMap;
    const ceilings = props.ceilings;
    const setSubjectPropertyHover = props.setSubjectPropertyHover;
    const selectedComps = props.selectedComps;
    const stableCentre = useMemo(() => ({ 
        lat: subjectProperty.addressDetails.latitude, 
        lng: subjectProperty.addressDetails.longitude 
    }), [subjectProperty.addressDetails]);

    const options = useMemo(() => ({
		disableDefaultUI: false,
		scrollwheel: true,
		mapTypeControl: false,
		streetViewControl: false,
        fullscreenControl: false,
        keyboardShortcuts: false,
        mapId: JSON.parse(process.env.REACT_APP_COMPS_MAP_ID).key
	}), []);

    const onLoad = (mapInstance) => {
        setRawMap(mapInstance);
    };

    const startPosition = {
        lat: subjectProperty.addressDetails.latitude,
        lng: subjectProperty.addressDetails.longitude
    };

    const checkBestComp = (item) => {
        const zpid = item.uniqueIds.zillowId;
        const bestIndex = selectedComps.findIndex(e => e.zpid === zpid && e.type === "arv");
        return bestIndex !== -1 ? true : false;
    };

    const checkCeilingComp = (item) => {
        const zpid = item.uniqueIds.zillowId;
        const ceilingIndex = ceilings.findIndex(e => e.zpid === zpid);
        return ceilingIndex !== -1 ? true : false;
    };

    const toggleSatellite = useCallback(() => {
        if (rawMap) {
            const newMapType = mapType === "roadmap" ? "satellite" : "roadmap";
            rawMap.setMapTypeId(newMapType);
            setMapType(newMapType);
        }
    }, [rawMap, mapType, setMapType]);

    const hoverTimeoutRef = useRef(null);

    const handleMouseEnter = () => {
        hoverTimeoutRef.current = setTimeout(() => {
            subjectPropertyHover();
        }, 300);
    };

    const handleMouseLeave = () => {
        clearTimeout(hoverTimeoutRef.current);
        setSubjectPropertyHover(false);
    };

    const subjectPropertyHover = () => {
        setSubjectPropertyHover(true);
    };

    return (
        isMapLoaded === true &&
        <GoogleMap
            zoom={14}
            center={stableCentre}
            mapContainerClassName="all-comps-map-container"
            options={options}
            onLoad={onLoad}
        >
            {
                rawMap && innerWidth >= 1000 &&
                <MapModeToggle
                    toggleSatellite={toggleSatellite}
                    mapType={mapType}
                />
            }
            {/* Hovered OR Normal property icon for Non-Best properties */}
            {
                rawMap && readOnly === false &&
                comps.map((item) =>
                    item.subjectProperty !== true && checkBestComp(item) === false &&
                    <Marker
                        map={rawMap}
                        position={{ lat: item.addressDetails.latitude, lng: item.addressDetails.longitude }}
                        onClick={() => viewComp(item)}
                        key={item.uniqueIds.zillowId}
                    >
                        <div className="relative-container">
                            <MapBubble
                                className=""
                                fill={checkCeilingComp(item) === true ? colour.red01 : colour.gray02}
                                stroke={checkCeilingComp(item) === true ? colour.red03 : colour.gray03}
                                strokeWidth="0.5"
                            />
                            <House
                                fill={checkCeilingComp(item) === true ? colour.red03 : colour.gray04}
                                className="all-comps-map-house-icon"
                            />
                        </div>
                    </Marker>
                )
            }
            {/* Hovered OR standard property icon for only Best properties */}
            {
                rawMap && readOnly === false &&
                comps.map((item) =>
                    item.subjectProperty !== true && checkBestComp(item) === true &&
                    <Marker
                        map={rawMap}
                        position={{ lat: item.addressDetails.latitude, lng: item.addressDetails.longitude }}
                        key={item.uniqueIds.zillowId}
                        onClick={() => viewComp(item)}
                    >
                        <div className="relative-container">
                            <MapBubble
                                className=""
                                fill={checkCeilingComp(item) === true && hoverZPID === item.uniqueIds.zillowId ? colour.red02 : checkCeilingComp(item) === true ? colour.red01 : hoverZPID === item.uniqueIds.zillowId ? colour.jade02 : colour.jade01}
                                stroke={checkCeilingComp(item) === true && hoverZPID === item.uniqueIds.zillowId ? colour.red03 : checkCeilingComp(item) === true ? colour.red02 : hoverZPID === item.uniqueIds.zillowId ? colour.jade03 : colour.jade02}
                                strokeWidth={checkCeilingComp(item) === true && hoverZPID === item.uniqueIds.zillowId ? "2" : checkCeilingComp(item) === true ? "0.5" : hoverZPID === item.uniqueIds.zillowId ? "2" : "0.5"}
                            />
                            <House
                                fill={checkCeilingComp(item) === true ? colour.red03 : colour.jade03}
                                className="all-comps-map-house-icon"
                            />
                        </div>
                    </Marker>
                )
            }
            {/* Clicked property for Best OR Normal */}
            {
                rawMap && readOnly === false && hoverZPID === null &&
                comps.map((item) =>
                    item.subjectProperty !== true && clickedZPID === item.uniqueIds.zillowId &&
                    <Marker
                        map={rawMap}
                        position={{ lat: item.addressDetails.latitude, lng: item.addressDetails.longitude }}
                        onClick={() => null}
                        key={item.uniqueIds.zillowId}
                    >
                        <div className="relative-container">
                            <MapBubble
                                className=""
                                fill={checkCeilingComp(item) === true ? colour.red01 : checkBestComp(item) === true ? colour.jade02 : colour.gray04}
                                stroke={checkCeilingComp(item) === true ? colour.red03 : checkBestComp(item) === true ? colour.jade03 : colour.gray05}
                                strokeWidth="2"
                            />
                            <House
                                fill={checkCeilingComp(item) === true ? colour.red03 : checkBestComp(item) === true ? colour.jade03 : colour.gray06}
                                className="all-comps-map-house-icon"
                            />
                        </div>
                    </Marker>
                )
            }
            {/* Shareable link showing different coloured icons */}
            {
                rawMap && readOnly === true && compTypeLegend !== null &&
                compTypeLegend.map((item) =>
                    item.comps.map((comp) =>
                        <Marker
                            map={rawMap}
                            position={{ lat: comp.addressDetails.latitude, lng: comp.addressDetails.longitude }}
                            onClick={() => viewComp(comp)}
                            key={comp.uniqueIds.zillowId}
                        >
                            <div className="relative-container">
                                <MapBubble
                                    className=""
                                    fill={(clickedZPID === comp.uniqueIds.zillowId && hoverZPID === null) || (comp.uniqueIds.zillowId === hoverZPID) ? item.stroke : item.fill}
                                    stroke={(clickedZPID === comp.uniqueIds.zillowId && hoverZPID === null) || (comp.uniqueIds.zillowId === hoverZPID) ? item.house : item.stroke}
                                    strokeWidth={(clickedZPID === comp.uniqueIds.zillowId && hoverZPID === null) || (comp.uniqueIds.zillowId === hoverZPID) ? "2" : "0.5"}
                                />
                                <House
                                    fill={item.house}
                                    className="all-comps-map-house-icon"
                                />
                            </div>
                        </Marker>
                    )
                )
            }
            {/* Subject property */}
            {
                rawMap &&
                <Marker
                    map={rawMap}
                    position={startPosition}
                    onClick={() => null}
                >
                    <div 
                        className="relative-container"
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    >
                        <MapBubble
                            className=""
                            fill={colour.gray07}
                            stroke={colour.gray00}
                            strokeWidth="0.5"
                        />
                        <House
                            fill={colour.gray00}
                            className="all-comps-map-house-icon"
                        />
                    </div>
                </Marker>
            }
            {
                showHeatmap === true &&
                <NeighbourhoodGradeMap
                    bounds={neighbourhoodBounds}
                    tracts={tracts}
                    neighbourhoodOption={0}
                    neighbourhoodData={neighbourhoodData}
                    clickedTract={null}
                    hoverMapLegend={hoverMapLegend}
                    setHoverMapLegend={setHoverMapLegend}
                />
            }
        </GoogleMap>
    )
};

export default React.memo(AllCompsMap);